import React, { useCallback, useEffect, useState } from 'react';
import { exerciseLibrary } from '../../services/api';
import { ConfirmDialog, Modal, errorList } from './shared';

/*
 * «Mini vídeos de licença aberta»: mini vídeos de demonstração para os exercícios do catálogo que ainda não
 * têm vídeo, a partir de DUAS fontes abertas — o wger.de (Workout Manager) e o Wikimedia Commons. O seletor
 * «Fonte dos vídeos» escolhe a fonte; o resto do fluxo é igual para as duas:
 *
 *   1. Pré-visualizar — não descarrega nem grava nada. Diz quantos exercícios seriam importados e o que fica
 *      de fora, com o motivo: sem correspondência segura (não se deduz nada de nomes parecidos), já tem vídeo
 *      (não se sobrepõe nada, nem aos vídeos de IA, que aqui não se apagam) ou erro. Mostra também quantos
 *      ficheiros a fonte publica e foram recusados (formato, tamanho ou duração) — o Commons tem lá ficheiros
 *      enormes, e o número não pode parecer uma avaria.
 *   2. Importar — só depois de uma confirmação explícita. Cada vídeo entra POR VALIDAR, com a LICENÇA e o
 *      AUTOR que a fonte declara ficheiro a ficheiro: nada chega ao atleta antes de uma pessoa ver o vídeo e
 *      confirmar que o movimento está certo — que é exactamente o que os vídeos de IA não davam.
 *
 * A fonte de cada ficheiro confirma a sua própria licença: no wger.de os vídeos são CC-BY-SA (hoje, todos 4.0,
 * do autor «Goulart»); no Commons só entra o que declare licença LIVRE (CC BY, CC BY-SA, CC0 ou domínio
 * público) — o que declare CC BY-NC/ND fica de fora.
 */

const SHOWN = 10;
const rowsWith = (report, action) => (report?.rows || []).filter((r) => r.action === action);
const first = (list) => list.slice(0, SHOWN);
const remaining = (list) => Math.max(0, list.length - SHOWN);
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const sizeOf = (bytes) => (bytes ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : null);

/**
 * As fontes. O wger.de é a omissão: os seus pedidos não levam `source`. As outras levam o seu nome, e é o
 * servidor que escolhe quem responde (a mesma rota, o mesmo diálogo).
 */
const SOURCES = {
  wger: {
    label: 'wger.de',
    consult: 'Não foi possível consultar o wger.de.',
    confirmNote:
      'a partir do wger.de (licença indicada item a item; hoje CC-BY-SA 4.0, com o autor registado)',
    intro: (
      <>
        Mini vídeos de demonstração do <strong>wger.de</strong> (Workout Manager), uma base de exercícios
        aberta cujos vídeos têm licença indicada item a item (hoje, todos <strong>CC-BY-SA 4.0</strong>).
      </>
    ),
    refused: (n) =>
      plural(n, 'vídeo da fonte recusado por formato ou tamanho', 'vídeos da fonte recusados por formato ou tamanho'),
    errorHint:
      'O motivo de cada um está na lista — nomeadamente os vídeos que a fonte publica em .MOV/HEVC ou acima do tecto da biblioteca.',
  },
  commons: {
    label: 'Wikimedia Commons',
    consult: 'Não foi possível consultar o Wikimedia Commons.',
    confirmNote:
      'a partir do Wikimedia Commons (licença LIVRE indicada ficheiro a ficheiro — CC BY, CC BY-SA, CC0 ou domínio público — com o autor registado)',
    intro: (
      <>
        Mini vídeos de demonstração do <strong>Wikimedia Commons</strong>, onde cada ficheiro declara a sua
        licença na própria página. Só entra o que declare uma licença <strong>livre</strong> (CC BY, CC BY-SA,
        CC0 ou domínio público); o que declare CC BY-NC/ND fica de fora.
      </>
    ),
    refused: (n) =>
      plural(n, 'ficheiro da fonte recusado por formato, tamanho ou duração', 'ficheiros da fonte recusados por formato, tamanho ou duração'),
    errorHint:
      'O motivo de cada um está na lista — o Commons tem lá ficheiros enormes (um vídeo de 1 h e 561 MB) que os tectos da biblioteca recusam.',
  },
  search: {
    label: 'Wikimedia Commons + Openverse',
    consult: 'Não foi possível consultar as fontes de pesquisa.',
    confirmNote:
      'a partir da pesquisa por nome no Wikimedia Commons e no Openverse (licença LIVRE indicada ficheiro a ficheiro — CC BY, CC BY-SA, CC0 ou domínio público — com o autor registado)',
    intro: (
      <>
        Procura por <strong>nome de cada exercício sem vídeo</strong> no <strong>Wikimedia Commons</strong> e no{' '}
        <strong>Openverse</strong>, em vez de ler uma categoria fixa. Só entra o que declare uma licença{' '}
        <strong>livre</strong>; o que declare CC BY-NC/ND, ou não declare licença nenhuma, fica de fora.
      </>
    ),
    refused: (n) =>
      plural(n, 'ficheiro da fonte recusado por licença, formato ou tamanho', 'ficheiros da fonte recusados por licença, formato ou tamanho'),
    errorHint:
      'O motivo de cada um está na lista. Um vídeo de outro exercício não entra pelas costas: o nome do ficheiro tem de ser o nome do exercício.',
  },
};

/** Uma lista de nomes (não uma tabela): cada exercício é um bloco que cabe em qualquer largura. */
function NameList({ label, rows, empty, showVideo = false }) {
  if (rows.length === 0) return <p className="lib-hint">{empty}</p>;
  return (
    <ul className="lib-batch-list" aria-label={label}>
      {first(rows).map((r) => (
        <li key={r.exerciseKey || r.exerciseName || r.externalId}>
          <div className="lib-batch-head">
            <strong>{r.exerciseName || r.exerciseKey}</strong>
            {showVideo && r.sizeBytes ? <span className="lib-hint"> · {sizeOf(r.sizeBytes)}</span> : null}
          </div>
          {r.externalName && <div className="lib-hint">Na fonte: {r.externalName}</div>}
          {r.reason && <div className="lib-hint">{r.reason}</div>}
        </li>
      ))}
      {remaining(rows) > 0 && <li><div className="lib-hint">e mais {remaining(rows)}.</div></li>}
    </ul>
  );
}

export default function OpenVideoImportDialog({ onClose, onImported }) {
  const [source, setSource] = useState('wger');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const spec = SOURCES[source];

  const preview = useCallback(async (src) => {
    setBusy(true);
    setError(null);
    try {
      // O wger.de é a fonte por omissão: os seus pedidos não levam `source`. As outras levam o seu nome.
      const { data } = src === 'wger'
        ? await exerciseLibrary.openVideoPreview()
        : await exerciseLibrary.openVideoPreview({ source: src });
      setReport(data);
    } catch (e) {
      // A pré-visualização anterior (se existir) fica à vista; fica o alerta e repete-se pelo botão.
      setError(errorList(e, SOURCES[src].consult).join(' '));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { preview(source); }, [preview, source]);

  // Trocar de fonte limpa o relatório da anterior: os números das duas fontes não se misturam.
  const changeSource = (next) => {
    if (next === source) return;
    setReport(null);
    setError(null);
    setConfirming(false);
    setSource(next);
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = source === 'wger'
        ? await exerciseLibrary.openVideoImport()
        : await exerciseLibrary.openVideoImport({ source });
      setReport(data);
      setConfirming(false);
      onImported?.(data);
    } catch (e) {
      // Nada se apaga: o relatório da pré-visualização continua à vista para se repetir a importação.
      setError(errorList(e, 'Não foi possível importar os vídeos.').join(' '));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const applied = !!report?.applied;
  const toImport = rowsWith(report, 'add');
  const skipped = rowsWith(report, 'skip');
  const failed = rowsWith(report, 'error');
  const withoutMatch = report?.withoutMatch || [];
  const count = report?.added ?? 0;
  const importLabel = `Importar para ${count === 1 ? '1 exercício' : `${count} exercícios`}`;

  const footer = (
    <>
      <button type="button" className="lib-btn" onClick={onClose}>Fechar</button>
      <button type="button" className="lib-btn" disabled={busy || applied} onClick={() => preview(source)}>Pré-visualizar de novo</button>
      <button type="button" className="lib-btn primary" disabled={busy || applied || !report || toImport.length === 0}
        onClick={() => setConfirming(true)}>
        {importLabel}
      </button>
    </>
  );

  return (
    <Modal title="Mini vídeos de demonstração (licença aberta)" onClose={onClose} footer={footer}>
      <div className="lib-field">
        <label className="lib-label" htmlFor="open-video-source">Fonte dos vídeos</label>
        <select id="open-video-source" className="lib-select" value={source} disabled={busy || applied}
          onChange={(e) => changeSource(e.target.value)}>
          <option value="wger">wger.de (Workout Manager) — vídeos CC-BY-SA</option>
          <option value="commons">Wikimedia Commons — categoria de demonstrações</option>
          <option value="search">Wikimedia Commons + Openverse — pesquisa pelo nome do exercício</option>
        </select>
      </div>

      <p className="lib-hint">
        {spec.intro} A pré-visualização não descarrega nem grava nada: mostra o que seria importado e o que fica
        de fora. Cada vídeo entra <strong>por validar</strong>, com a licença e o autor gravados — só chega aos
        atletas depois de uma pessoa ver o vídeo e confirmar que o movimento está certo. O que a fonte publica em
        .MOV/HEVC, acima do tecto da biblioteca ou com mais de 30 s é <strong>convertido</strong> em vez de
        recusado, e do vídeo sai também a <strong>imagem de capa</strong> (por validar) quando o exercício ainda
        não tem nenhuma imagem aprovada.
      </p>

      {error && <div className="lib-notice bad" role="alert">{error}</div>}
      {busy && !report && <div className="lib-hint" role="status">A consultar {spec.label}…</div>}

      {report && (
        <div data-testid="open-video-report">
          <div className="lib-banner" role="status">
            <strong>{applied ? 'Importação concluída' : 'Pré-visualização'}</strong>
            <span data-testid="open-video-total">{plural(report.total, 'exercício do catálogo', 'exercícios do catálogo')}</span>
            <span data-testid="open-video-added">{plural(report.added, 'exercício', 'exercícios')} {applied ? 'importado' : 'a importar'}</span>
            <span data-testid="open-video-skipped">{plural(report.skipped, 'ignorado', 'ignorados')}</span>
            <span data-testid="open-video-errors">{report.errors} com erro</span>
            <span data-testid="open-video-unmatched">{report.unmatched} sem correspondência segura</span>
            <span data-testid="open-video-refused">{spec.refused(report.refusedByFormat)}</span>
            {report.refusedByLicense > 0 && (
              <span data-testid="open-video-refused-license">
                {plural(report.refusedByLicense, 'recusado por licença', 'recusados por licença')}
              </span>
            )}
            {(report.transcoded > 0 || report.transcodable > 0) && (
              <span data-testid="open-video-transcoded">
                {report.transcoded > 0
                  ? plural(report.transcoded, 'vídeo recuperado por conversão', 'vídeos recuperados por conversão')
                  : plural(report.transcodable, 'vídeo precisa de conversão', 'vídeos precisam de conversão')}
                {report.transcodeUnavailable > 0 && ` · ${report.transcodeUnavailable} sem conversão possível neste servidor`}
              </span>
            )}
            {report.posters > 0 && (
              <span data-testid="open-video-posters">
                {plural(report.posters, 'imagem de capa tirada do vídeo', 'imagens de capa tiradas dos vídeos')}
              </span>
            )}
          </div>

          {(report.notes || []).length > 0 && (
            <div className="lib-notice warn" role="status" data-testid="open-video-notes">
              {report.notes.map((note) => <div key={note}>{note}</div>)}
            </div>
          )}

          <p className="lib-hint">
            Fonte: {report.attribution || spec.label} · licença: {report.license || 'não indicada'}
            {report.sourceUrl && <> · <a href={report.sourceUrl} target="_blank" rel="noopener noreferrer">página de origem</a></>}
          </p>

          {report.existingAiVideos > 0 && (
            <div className="lib-notice warn" role="status" data-testid="open-video-ai">
              Há {plural(report.existingAiVideos, 'vídeo gerado por IA', 'vídeos gerados por IA')} na biblioteca.
              Nenhum foi tocado: um exercício que já tem vídeo fica de fora desta importação. Substituí-los é
              decisão de quem gere a biblioteca, não desta importação.
            </div>
          )}

          {applied ? (
            <>
              <div className="lib-notice warn" role="status" data-testid="open-video-pending">
                <strong>{plural(report.added, 'exercício ficou', 'exercícios ficaram')} por validar.</strong>{' '}
                Os vídeos que entraram não chegam aos atletas antes de uma pessoa os ver e aprovar: abra o
                exercício na lista, na secção «Imagens» (o ficheiro está marcado «Vídeo»), confirme o movimento
                e aprove — ou rejeite, dizendo porquê.
              </div>
              {failed.length > 0 && (
                <>
                  <h3 className="lib-h3">Ficaram por importar, com erro ({failed.length})</h3>
                  <NameList label="Exercícios que ficaram por importar" rows={failed} empty="Nenhum erro." />
                </>
              )}
            </>
          ) : (
            <>
              <h3 className="lib-h3">Seria importado ({toImport.length} exercícios)</h3>
              <NameList label="Exercícios que seriam importados" rows={toImport} showVideo
                empty="Nenhum exercício do catálogo sem vídeo tem correspondência na fonte." />

              <h3 className="lib-h3">Fica de fora: sem correspondência segura ({withoutMatch.length})</h3>
              <p className="lib-hint">Nada lhes é tocado. Sem correspondência segura pelo nome em inglês ou pelos alternativos, não se importa nada.</p>
              <NameList label="Exercícios sem correspondência segura" rows={withoutMatch} empty="Todos os exercícios sem vídeo encontraram correspondência." />

              <h3 className="lib-h3">Fica de fora: com erro ({failed.length})</h3>
              <p className="lib-hint">{spec.errorHint}</p>
              <NameList label="Exercícios com erro" rows={failed} empty="Nenhum erro." />

              <details className="lib-hint">
                <summary>Fica de fora: ignorados ({skipped.length}) — o motivo de cada um está na lista</summary>
                <NameList label="Exercícios ignorados" rows={skipped} empty="Nenhum." />
              </details>
            </>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Importar mini vídeos de licença aberta"
          message={`Vão ser importados mini vídeos de demonstração de ${count === 1 ? '1 exercício' : `${count} exercícios`} do catálogo, ${spec.confirmNote}. Cada vídeo entra POR VALIDAR e não chega aos atletas antes de uma pessoa ver o movimento e o aprovar. Nada é sobreposto: os exercícios que já têm vídeo ficam de fora.`}
          confirmLabel="Importar"
          busy={busy}
          onConfirm={apply}
          onCancel={() => setConfirming(false)}
        />
      )}
    </Modal>
  );
}
